import { Component, OnInit, inject, computed, effect } from '@angular/core';
import { Fullscreen } from '@boengli/capacitor-fullscreen';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { LayoutComponent } from "./core/components/layout/layout.component";
import { CapacitorService } from './core/services/mobile/capacitor.service';
import { ApkDownloadService } from './core/services/mobile/apk-download.service';
import { UpdateCheckService } from './core/services/mobile/update-check.service';
import { UpdateDialogComponent, UpdateDialogConfig } from './shared/update-dialog/update-dialog.component';
import { AuthStore } from './core/store/auth/auth.store';
import { ScrollService } from './core/services/scroll.service';

@Component({
  selector: 'app-root',
  imports: [InputTextModule, ButtonModule, DialogModule, LayoutComponent, UpdateDialogComponent],
  template: `
    <app-layout></app-layout>
    
    @if (updateDialogConfig()) {
      <app-update-dialog 
        [config]="updateDialogConfig()!"
        (dismissed)="onUpdateDialogDismissed()"
        (downloadComplete)="onDownloadComplete()"
        (downloadError)="onDownloadError($event)"
      />
    }
  `,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly _capacitorService = inject(CapacitorService);
  private readonly _apkDownloadService = inject(ApkDownloadService);
  private readonly _updateCheckService = inject(UpdateCheckService);
  private readonly _authStore = inject(AuthStore);
  private readonly _scrollService = inject(ScrollService);

  private _updateCheckDone = false;

  /**
   * Computed signal for update dialog configuration
   * Creates dialog config when update is available and dialog should be visible
   */
  readonly updateDialogConfig = computed<UpdateDialogConfig | null>(() => {
    const state = this._updateCheckService.updateState();

    if (state.dialogVisible && state.availableUpdate) {
      return {
        versionInfo: state.availableUpdate,
        visible: true
      };
    }

    return null;
  });

  constructor() {
    // Monitor authentication state and check for updates when user logs in
    effect(() => {
      const isAuthenticated = this._authStore.isAuthenticated();

      // Only check for updates once when user becomes authenticated
      if (isAuthenticated && !this._updateCheckDone && this._capacitorService.isNative()) {
        this._updateCheckDone = true;
        this._checkForUpdates();
      }
    });
  }

  ngOnInit() {
    if (this._capacitorService.isNative()) {
      this._activateImmersiveMode();
      this._cleanupCachedApk();
    }
  }

  private async _activateImmersiveMode(): Promise<void> {
    await this._capacitorService.initializeApp();

    try {
      await Fullscreen.activateImmersiveMode();
    } catch (error) {
    }
  }

  /**
   * Clean up cached APK files on app startup
   * This ensures old APK files are removed after installation
   */
  private async _cleanupCachedApk(): Promise<void> {
    try {

      // Wait for Capacitor to be fully initialized
      await this._capacitorService.initializeApp();

      // Perform cleanup within 5 seconds of app start
      const cleanupStartTime = Date.now();
      await this._apkDownloadService.cleanupCachedApk();
      const cleanupDuration = Date.now() - cleanupStartTime;


    } catch (error) {
      // Log error but don't block app startup
      console.error('⚠️ [AppComponent] APK cleanup failed (non-critical):', error);
    }
  }

  /**
   * Check for app updates when user is authenticated
   * This runs only when user is logged in (API requires authentication)
   */
  private async _checkForUpdates(): Promise<void> {
    try {
      // Wait for Capacitor to be fully initialized
      await this._capacitorService.initializeApp();

      // Check for updates
      await this._updateCheckService.checkForUpdates();


    } catch (error) {
      // Log error but don't block app usage
      console.error('⚠️ [AppComponent] Update check failed (non-critical):', error);
    }
  }

  /**
   * Handle update dialog dismissed event
   * Called when user closes optional update dialog
   */
  onUpdateDialogDismissed(): void {
    this._updateCheckService.dismissUpdateDialog();
  }

  /**
   * Handle download complete event
   * Called when APK download completes successfully
   */
  onDownloadComplete(): void {
    // Close the dialog after installation starts
    // The Android Package Installer will take over from here
    // this._updateCheckService.dismissUpdateDialog();
  }

  /**
   * Handle download error event
   * @param error Error message from download operation
   */
  onDownloadError(error: string): void {
    console.error('❌ [AppComponent] Update download error:', error);
    // Error is already displayed in the dialog, just log it here
  }
}
